import type { ComponentProps } from 'react';
import { type Placement } from '@floating-ui/react-dom';
import { forwardRef } from 'react';

import { ButtonIcon, ButtonProps } from '../Button';
import { cn } from '../cn';
import { buttonHeight, buttonHPadding, buttonTextAndBorder } from '../common';
import { General, Shapes } from '../StrokeIcons';
import { InputProps } from './Input';
import InputTitle from './InputTitle';

export type SelectValueType = {
  value: string;
  label: string;
  desc?: string;
  icon?: React.JSX.Element;
  iconTooltip?: React.JSX.Element | string;
  // _createRow?: boolean;
};

export type CheckboxesProps<OptionType = SelectValueType> = Omit<InputProps, 'rIcon' | 'placeholder'> & {
  text?: ButtonProps['text'];
  placement?: Placement;
  options: OptionType[];
  value?: string;
  uncontrolled?: boolean;
  defaultValues?: string[];
};

const Checkboxes = forwardRef<HTMLDivElement, CheckboxesProps>(
  (
    {
      className,
      size,
      error,
      title,
      disabled,
      text,
      titleClassName,
      name,
      value,
      onChange,
      options,
      uncontrolled,
      defaultValues,
    },
    ref,
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          'inline-flex min-w-28 flex-col text-slate-600 dark:text-slate-100',
          { 'text-stone-400 dark:text-slate-700': disabled },
          {
            'gap-1': size === 'md' || size == null,
            'min-w-22 gap-0.5': size === 'sm',
            'min-w-18 gap-0': size === 'xs',
          },
          className,
        )}>
        {title != null && (
          <InputTitle error={error} size={size} className={titleClassName}>
            {title}
          </InputTitle>
        )}
        <div
          className={cn(
            'inline-flex shrink-0 flex-col overflow-hidden font-sans transition-colors',
            {
              'bg-white focus-within:bg-apricot-white-50': !disabled,
              'bg-stone-50': disabled,
              'dark:bg-charade-950 dark:focus-within:bg-charade-800': !disabled,
              'dark:bg-slate-950': disabled,
            },
            {
              'border border-slate-200 dark:border-charade-700': !text,
              'border-fair-pink-300 dark:border-fair-pink-900': !text && error,
              'bg-fair-pink-100 text-fair-pink-600 dark:bg-fair-pink-950 dark:text-fair-pink-700 dark:focus-within:bg-fair-pink-900':
                error,
            },
            buttonTextAndBorder(size),
          )}>
          {options?.map((option) => (
            <Checkbox
              key={option.value}
              icon={option.icon}
              checked={value === option.value}
              value={option.value}
              disabled={disabled}
              onChange={onChange}
              name={name}
              size={size}
              uncontrolled={uncontrolled}
              defaultChecked={defaultValues?.includes(option.value)}>
              {option.label}
            </Checkbox>
          ))}
        </div>
      </div>
    );
  },
);

Checkboxes.displayName = 'Checkboxes';

export default Checkboxes;

type CheckboxProps = Omit<ComponentProps<'input'>, 'size' | 'value'> & {
  size?: InputProps['size'];
  value: string;
  icon?: React.JSX.Element;
  uncontrolled?: boolean;
  defaultChecked?: boolean;
};

export function Checkbox({
  children,
  disabled,
  checked,
  value,
  name,
  onChange,
  size,
  icon,
  uncontrolled,
  defaultChecked,
}: CheckboxProps) {
  return (
    <label
      className={cn(
        'inline-flex cursor-pointer overflow-hidden border-b font-sans transition-colors last:border-none',
        'border-slate-200 hover:bg-haiti-100 dark:border-slate-700',
        { 'bg-haiti-50 dark:bg-haiti-950': checked },
        buttonHeight(size),
      )}>
      <input
        disabled={disabled}
        type="checkbox"
        checked={uncontrolled ? undefined : checked}
        value={value || ''}
        defaultChecked={defaultChecked}
        onChange={onChange}
        className="hidden peer"
        name={name}
      />

      {!uncontrolled && (
        <ButtonIcon className={cn({ 'text-haiti-500': checked })} size={size}>
          {checked ? <General.CheckSquare /> : <Shapes.Square />}
        </ButtonIcon>
      )}

      {uncontrolled && (
        <>
          <ButtonIcon className={'peer-checked:text-haiti-500 hidden peer-checked:block'} size={size}>
            <General.CheckSquare />
          </ButtonIcon>
          <ButtonIcon className={'peer-checked:hidden'} size={size}>
            <Shapes.Square />
          </ButtonIcon>
        </>
      )}

      <div className={cn('flex h-full shrink grow items-center truncate', buttonHPadding(size))}>{children}</div>
      {icon && <ButtonIcon size={size}>{icon}</ButtonIcon>}
    </label>
  );
}
